import { Component, type ReactNode, type ErrorInfo } from "react";

interface FallbackArgs {
  error: Error | null;
  reset: () => void;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((args: FallbackArgs) => ReactNode);
  onError?: (error: Error, info: ErrorInfo) => void;
  resetKeys?: ReadonlyArray<unknown>;
  sectionLabel?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const reportedKey = "__vnz_error_reported__";
const chunkReloadKey = "vnz_chunk_reload";
const CHUNK_RELOAD_GUARD_MS = 30_000;

const BUILD_ID: string = import.meta.env.VITE_BUILD_ID || "dev";

interface ReporterAuthContext {
  storeId: string | null;
  userId: string | null;
}

let authContext: ReporterAuthContext = { storeId: null, userId: null };

export function setReporterAuth(ctx: { storeId?: string | null; userId?: string | null }) {
  authContext = { storeId: ctx.storeId ?? null, userId: ctx.userId ?? null };
}

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: unknown; message?: unknown };
  const name = typeof e.name === "string" ? e.name : "";
  const msg = typeof e.message === "string" ? e.message : String(err);
  if (name === "ChunkLoadError") return true;
  return (
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

function recentReloadAttempt(): boolean {
  try {
    const raw = sessionStorage.getItem(chunkReloadKey);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < CHUNK_RELOAD_GUARD_MS;
  } catch {
    return false;
  }
}

function maybeReloadForChunkError(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;
  if (recentReloadAttempt()) return false;
  try {
    sessionStorage.setItem(chunkReloadKey, String(Date.now()));
  } catch {}
  try {
    window.location.reload();
  } catch {}
  return true;
}

export function clearChunkReloadGuard() {
  try {
    sessionStorage.removeItem(chunkReloadKey);
  } catch {}
}

const APP_ORIGIN: string | null = typeof window !== "undefined" && window.location ? window.location.origin : null;

function isNoiseError(payload: { message: string | null; stack: string | null; errorName: string | null }): boolean {
  const msg = payload.message || "";
  const stack = payload.stack || "";
  const hasMeaningfulStack = stack.trim().length > 0 && stack.trim() !== "null";

  if (!hasMeaningfulStack && /^Script error\.?$/i.test(msg)) {
    return true;
  }
  if (/window\.__firefox__|window\.ethereum|window\.webkit\.messageHandlers|__firefox__\.reader/i.test(msg)) {
    return true;
  }
  if (/disconnected port object/i.test(msg)) {
    return true;
  }
  // WebView do Android reciclando a ponte JS quando o app vai pro fundo —
  // não é erro do sistema e não tem ação possível (6 ocorrências na 1ª hora
  // de telemetria viva, 27/07).
  if (/Java object is gone|Error invoking postMessage/i.test(msg)) {
    return true;
  }
  if (msg.startsWith("[image-normalize] Formato HEIC/HEIF não suportado")) {
    return true;
  }
  if (hasMeaningfulStack && /chrome-extension:\/\/|moz-extension:\/\/|safari-extension:\/\//i.test(stack)) {
    const hasAppFrame = !!APP_ORIGIN && stack.includes(APP_ORIGIN);
    if (!hasAppFrame) {
      return true;
    }
  }
  if (hasMeaningfulStack && !/https?:\/\//i.test(stack) && /<anonymous>/i.test(stack)) {
    return true;
  }
  if (/setContactAutofillValuesFromBridge|__REACT_DEVTOOLS_GLOBAL_HOOK__|MetaMask|tronWeb/i.test(stack)) {
    return true;
  }
  return false;
}

// Devolve se o registro chegou na API (false = sem rede ou recusado): quem precisa guardar e reenviar depois (o aviso
// de conexão, lib/avisoConexao) usa isso; os outros chamadores ignoram, como sempre.
export function reportError(
  error: Error,
  componentStack: string | null | undefined = null,
  sectionLabel: string | null | undefined = null,
): Promise<boolean> {
  try {
    const message = error?.message || String(error);
    const messageWithSection = sectionLabel ? `[${sectionLabel}] ${message}` : message;
    const payload = {
      message: messageWithSection,
      errorName: error?.name || "Error",
      stack: error?.stack || null,
      componentStack: componentStack || null,
      url: typeof window !== "undefined" ? window.location.href : null,
      pathname: typeof window !== "undefined" ? window.location.pathname : null,
      viewportWidth: typeof window !== "undefined" ? window.innerWidth : null,
      viewportHeight: typeof window !== "undefined" ? window.innerHeight : null,
      appVersion: BUILD_ID,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      storeId: authContext.storeId || null,
      userId: authContext.userId || null,
    };

    if (isNoiseError(payload)) {
      return Promise.resolve(true);
    }

    const body = JSON.stringify(payload);
    // URL relativa batia no Worker da borda (vendanozap.app) e morria no
    // fallback SPA com 405 — nenhum erro de cliente chegou no banco entre o
    // split de domínio (20/05/2026) e este fix. Em prod o report precisa ir
    // direto pra API (VITE_API_URL); em dev a base vazia mantém o proxy Vite.
    // sendBeacon foi removido: cross-origin com Blob JSON exige preflight e
    // falha silenciosa (retorna true e descarta); fetch keepalive cobre o
    // mesmo caso de unload e reporta falha no .catch.
    const apiBase = ((import.meta.env.VITE_API_URL as string | undefined) || "")
      .trim()
      .replace(/\/+$/, "");
    const url = `${apiBase}/api/client-errors`;

    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).then((r) => r.ok, () => false);
  } catch {
    // never throw from the reporter
    return Promise.resolve(false);
  }
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error) && !recentReloadAttempt()) {
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) return;
    const { resetKeys } = this.props;
    const prevKeys = prevProps.resetKeys;
    if (!resetKeys && !prevKeys) return;
    let changed = false;
    if (!resetKeys || !prevKeys) {
      changed = true;
    } else if (resetKeys.length !== prevKeys.length) {
      changed = true;
    } else {
      for (let i = 0; i < resetKeys.length; i++) {
        if (!Object.is(resetKeys[i], prevKeys[i])) {
          changed = true;
          break;
        }
      }
    }
    if (changed) {
      this.setState({ hasError: false, error: null });
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      "[ErrorBoundary]",
      this.props.sectionLabel || "(root)",
      error?.message,
      error?.stack,
      info?.componentStack,
    );
    if (maybeReloadForChunkError(error)) {
      reportError(error, info?.componentStack, this.props.sectionLabel);
      return;
    }
    try {
      sessionStorage.setItem(reportedKey, "1");
    } catch {}
    reportError(error, info?.componentStack, this.props.sectionLabel);
    try {
      this.props.onError?.(error, info);
    } catch {
      // never throw from the boundary
    }
  }

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (fallback !== undefined) {
        if (typeof fallback === "function") {
          return fallback({ error: this.state.error, reset: this.reset });
        }
        return fallback;
      }
      return (
        <div className="h-svh w-full flex flex-col items-center justify-center bg-background text-foreground p-6 text-center">
          <p className="text-xl font-semibold mb-2">Não foi possível carregar a página</p>
          <p className="text-muted-foreground mb-6">Algo inesperado quebrou. Recarregue pra tentar de novo.</p>
          <button
            onClick={() => {
              this.reset();
              window.location.reload();
            }}
            className="px-6 py-2 bg-primary text-white rounded-md font-medium hover:bg-primary/90 transition-colors"
          >
            Recarregar página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export function SectionErrorBoundary({
  children,
  label,
  resetKeys,
  compact = false,
  anchorId,
}: {
  children: ReactNode;
  label: string;
  resetKeys?: ReadonlyArray<unknown>;
  compact?: boolean;
  anchorId?: string;
}) {
  const content = (
    <ErrorBoundary
      sectionLabel={label}
      resetKeys={resetKeys}
      fallback={({ reset }) => (
        <div
          role="alert"
          className={`w-full rounded-xl border border-destructive/30 bg-destructive/5 text-foreground ${
            compact ? "p-3" : "p-4"
          } flex flex-col items-start gap-2`}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-destructive">
              Não foi possível carregar esta seção
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {label ? `Houve um erro ao exibir "${label}". ` : ""}
            O restante da tela continua funcionando.
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-1 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
  // anchorId: ancora pro scrollspy da barra das Configuracoes; scroll-mt
  // compensa a barra sticky + topbar mobile ao rolar ate a secao.
  return anchorId ? (
    <div id={anchorId} className="scroll-mt-28 md:scroll-mt-16">
      {content}
    </div>
  ) : (
    content
  );
}

if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    const err = event.error instanceof Error ? event.error : new Error(event.message || "window.onerror");
    if (maybeReloadForChunkError(err)) {
      reportError(err, null);
      return;
    }
    reportError(err, null);
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const err = reason instanceof Error ? reason : new Error(typeof reason === "string" ? reason : JSON.stringify(reason));
    if (maybeReloadForChunkError(err)) {
      reportError(err, null);
      return;
    }
    reportError(err, null);
  });
}
